import { isPlatformServer } from "@angular/common";
import { effect, Inject, Injectable, makeStateKey, PLATFORM_ID, signal, TransferState } from '@angular/core';
import { Observable } from "rxjs";
import {
  DistinctProductFilter,
  HeaderAndFooterResponseInterface,
  LoadResponseInterface,
  ProductAttributeInterface,
  ProductCollection,
  WagtailCategoryResponseInterface,
  WagtailPageContentElementInterface,
  WagtailPageResponseInterface,
  WagtailProductsCarouselInterface,
  WagtailProductsRendererInterface
} from "@gea-waldviertler-website/gea-models";
import { HttpWagtailService, SimpleKeyValueInterface } from "./http/wagtail.service";
import { PageService } from "@gea-waldviertler-website/shop-lib";
import { HttpProductService } from "./http/product.service";

const CMS_PAGE_KEY = makeStateKey<WagtailPageResponseInterface>('cms_page');
const HEADER_AND_FOOTER_KEY = makeStateKey<HeaderAndFooterResponseInterface>('cms_header_and_footer')
@Injectable({
  providedIn: 'root'
})
export class WagtailService {

  // mapcache
  _cachedCmsPages: Map<string, WagtailPageResponseInterface>
  _cachedCmsCategoryPages: Map<string, WagtailCategoryResponseInterface>
  _cachedMenuAndFooter: HeaderAndFooterResponseInterface | undefined = undefined

  private pageSignal = signal<WagtailPageResponseInterface | undefined>(undefined)

  constructor(
    @Inject(HttpWagtailService) private httpService: HttpWagtailService,
    @Inject(PLATFORM_ID) private platformId: typeof PLATFORM_ID,
    private transferState: TransferState,
    @Inject(HttpProductService) private productService: HttpProductService,
    @Inject(PageService) private pageService: PageService,
) {
    this._cachedCmsPages = new Map()
    this._cachedCmsCategoryPages = new Map()

    effect(() => {
      let se = this.pageService.getActiveShopEventsSignal()()
      this.pageSignal.update((pr) => {
        if (! pr) return pr

        for (let contentElement of pr.content['landing']) {
          if (contentElement.type == "shop_productcarousel") {
            if (contentElement.value.hasOwnProperty('products')) {
              (contentElement as WagtailPageContentElementInterface<WagtailProductsCarouselInterface>).value.products.applyShopEvents(se)
            }
          }
        }
        return pr
      })
    })
  }

  loadPage(pageId: number, params?: SimpleKeyValueInterface) {
    return new Observable<LoadResponseInterface>(subscriber => {
      this.getPageContents(pageId, params).subscribe(pageResponse => {
        // enrich products with current data from shop-api

        // pick all products from contentElements having productfilters assigned
        let productFilters: DistinctProductFilter[] = []
        for (const contentElement of pageResponse.content['landing']) {
          // currently on wagtail content element "shop_productchooser" only
          if (["shop_productchooser"].includes(contentElement.type)) {
            productFilters.push(... (contentElement.value as WagtailProductsRendererInterface).products)
          }
        }

        // if products has been found, enrich
        if (productFilters.length > 0) {
          let contentLanding = []
          // fetch products from fundi if any and add to collection
          this.productService.getProducts(productFilters).subscribe(response => {
            let productCollection = new ProductCollection(response.products.productList)
            let productAttributes = new Map<string, ProductAttributeInterface>(response.products.productAttributes)

            // apply product stock
            if (response.products.productList && response.stock) {
              for (let product of productCollection) {
                for (const stock of response.stock) {
                  if (stock.productId == product.productId) {
                    product.applyStock(stock)
                  }
                }
              }
            }

            // apply shop events
            const activeEvents = this.pageService.getActiveShopEventsSignal()()
            if (activeEvents) {
              productCollection.applyShopEvents(activeEvents)
            } else {
              productCollection.applyShopEvents(null)
            }

            for (let contentElement of [ ... pageResponse.content['landing']]) {
              // currently on wagtail content element "shop_productchooser" only
              if (contentElement.type == "shop_productchooser") {
                const val = contentElement.value as WagtailProductsRendererInterface
                contentElement = contentElement as unknown as WagtailPageContentElementInterface<WagtailProductsCarouselInterface>
                contentElement.type = "shop_productcarousel"
                let value: WagtailProductsCarouselInterface = {
                  columns: val.columns,
                  title: val.title,
                  limit: val.limit,
                  products: new ProductCollection(),
                  attributes: productAttributes
                }

                for (const filter of val.products) {
                  const p = productCollection.get(filter.productId)
                  if (p) value.products.add(p)
                }
                contentElement.value = value
              }
              contentLanding.push(contentElement)
            }

            pageResponse.content['landing'] = contentLanding

            this.pageSignal.set(pageResponse)
            subscriber.next({success: true})
          }, err => {
            this.pageSignal.set(pageResponse)
            subscriber.next({code: 200, success: false, message: err.message ? err.message : 'Could not load products!'})
          })
        } else {
          this.pageSignal.set(pageResponse)
          subscriber.next({success: true})
        }
      }, (err) => {
        subscriber.next({success: false, code: 404, message: err.message ? err.message : 'unknown'})
      })
    })
  }

  getPageSignal() {
    return this.pageSignal.asReadonly()
  }

  private getPageContents(pageId: number, params?: SimpleKeyValueInterface) : Observable<WagtailPageResponseInterface> {
    // check cache
    const cachekey = pageId + (params ? params.toString() : '')
    if (this._cachedCmsPages.has(cachekey)) {
      const c = this._cachedCmsPages.get(cachekey)
      if (c) {
        return new Observable<WagtailPageResponseInterface>(s => {
          s.next(c)
        })
      }
    }

    let pageObservable: Observable<WagtailPageResponseInterface>

    // check ssr
    if (this.transferState.hasKey(CMS_PAGE_KEY)) {
      const contents = this.transferState.get<WagtailPageResponseInterface | undefined>(CMS_PAGE_KEY, undefined)
      this.transferState.remove(CMS_PAGE_KEY)
      if (contents) {
        pageObservable = new Observable<WagtailPageResponseInterface>((subscriber) => {
          subscriber.next(contents)
        })
      }

    // fetch from api
    } else {
      pageObservable = this.httpService.getPage(pageId)
    }

    return new Observable<WagtailPageResponseInterface>((subscriber) => {
      pageObservable.subscribe(contents => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(CMS_PAGE_KEY, contents)
        }
        this._cachedCmsPages.set(cachekey, contents)
        subscriber.next(contents)
      })
    })
  }

  getHeaderAndFooter(): Observable<HeaderAndFooterResponseInterface> {
    // check cache
    if (this._cachedMenuAndFooter) {
      const c = this._cachedMenuAndFooter
      return new Observable<HeaderAndFooterResponseInterface>(s => {
        s.next(c)
      })
    }

    let obs: Observable<HeaderAndFooterResponseInterface>

    // check ssr
    if (this.transferState.hasKey(HEADER_AND_FOOTER_KEY)) {
      const contents = this.transferState.get<HeaderAndFooterResponseInterface | undefined>(HEADER_AND_FOOTER_KEY, undefined)
      if (contents) {
        obs = new Observable<HeaderAndFooterResponseInterface>((subscriber) => {
          subscriber.next(contents)
        })
      }
      this.transferState.remove(HEADER_AND_FOOTER_KEY)

      // fetch from api
    } else {
      obs = this.httpService.getHeaderAndFooter()
    }

    return new Observable<HeaderAndFooterResponseInterface>((subscriber) => {
      obs.subscribe({
        next: contents => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(HEADER_AND_FOOTER_KEY, contents)
          }
          this._cachedMenuAndFooter = contents
          subscriber.next(contents)
        },
        error: err => subscriber.error(err),
        complete: () => subscriber.complete(),
      })
    })
  }
}
